/**
 * Provenance marker for a participant hold placed as part of rejecting an application.
 *
 * `holdTasParticipantRequest` carries nothing but a free-text `note`, so that note is
 * the only channel available for recording *why* a participant was held. Reopening a
 * rejected application must release the hold only when that same rejection placed it —
 * an employee already held for an unrelated reason has to stay held — which means the
 * hold needs a signal a later reader can recognise.
 *
 * The prefix is deliberately human-readable rather than an opaque token: it renders
 * acceptably wherever participant notes are displayed, while still being matchable.
 * Keep {@link REJECT_HOLD_NOTE_PREFIX} and {@link rejectHoldNote} in lockstep with the
 * backend, which writes the identical prefix when it holds on the automated-reviewer
 * path (tuitionio/tio-api#10006) — a reopen cannot tell the two apart, and must not
 * have to.
 */
export const REJECT_HOLD_NOTE_PREFIX = 'Rejected application';

/**
 * Build the note for a reject-time hold.
 *
 * @param applicationId the application being rejected
 * @param note the approver's free-text rejection feedback, if any
 */
export function rejectHoldNote(applicationId: string, note?: string | null) {
  const prefix = `${REJECT_HOLD_NOTE_PREFIX} ${applicationId}.`;
  const trimmed = note?.trim();

  return trimmed ? `${prefix} ${trimmed}` : prefix;
}

/**
 * Whether a participant hold note was written by rejecting the given application.
 *
 * Scoped to the application id on purpose: a participant may have been held by an
 * earlier rejection of a *different* application, and reopening this one must not
 * release that hold.
 */
export function isRejectHoldNoteFor(applicationId: string, note?: string | null) {
  return Boolean(note?.startsWith(`${REJECT_HOLD_NOTE_PREFIX} ${applicationId}.`));
}
